<template>
    <div class="pb-10">
        <v-row dense class="mb-4">
            <v-col cols="12" md="2" />
            <v-col cols="12" md="4">
                <v-autocomplete
                    ref="option"
                    v-model="contract.variantId"
                    :items="variants"
                    item-title="title"
                    item-value="value"
                    closable-chips
                    hide-details
                    clearable
                    :label="$t('product_variant')"
                    density="compact"
                    color="primary"
                    data-cy="airline-code"
                />
            </v-col>
        </v-row>
        <v-row dense>
            <v-col cols="12" md="2" />
            <v-col cols="12" sm="3" md="2" class="pb-4">
                <v-radio-group v-model="contract.sharingType" :disabled="editMode" density="compact" hide-details>
                    <v-radio
                        v-for="{value, title} in useSharing"
                        :key="value"
                        :label="title"
                        :value="value"
                        color="primary"
                        :data-cy="value"
                    />
                </v-radio-group>
            </v-col>
            <v-col cols="12" sm="3" md="2" class="pb-4">
                <v-radio-group
                    v-model="contract.transportationType"
                    :disabled="editMode"
                    density="compact"
                    hide-details
                >
                    <v-radio
                        v-for="{value, title} in useTransportation"
                        :key="value"
                        :label="title"
                        :value="value"
                        color="primary"
                        :data-cy="value"
                    />
                </v-radio-group>
            </v-col>
        </v-row>
        <v-row dense class="pb-6">
            <v-col cols="12" md="2" />
            <v-col>
                <div class="text-subtitle-1">{{ $t('duration') }}:</div>
                <v-radio-group
                    v-model="contract.singleDay"
                    density="compact"
                    hide-details
                    @update:model-value="contract.duration = null"
                >
                    <v-row dense class="d-flex align-center" style="height: 50px">
                        <v-col cols="12" sm="3" md="2">
                            <v-radio
                                :label="$t('single_day')"
                                :value="true"
                                color="primary"
                                data-cy="single-day"
                                density="compact"
                            />
                        </v-col>
                        <v-col cols="12" sm="3" md="2">
                            <v-text-field
                                v-if="contract.singleDay"
                                v-model="contract.duration"
                                label="(HH:mm)"
                                :rules="[timeFormatRule()]"
                                variant="outlined"
                                color="primary"
                                density="compact"
                                data-cy="start-time"
                                maxlength="5"
                                hide-details
                                width="100"
                                @update:model-value="e => formatTime(e, 'duration')"
                            />
                        </v-col>
                    </v-row>
                    <v-row dense class="d-flex align-center" style="height: 50px">
                        <v-col cols="12" sm="3" md="2">
                            <v-radio
                                :label="$t('multi_day_tour')"
                                :value="false"
                                color="primary"
                                data-cy="multi-day-tour"
                                density="compact"
                            />
                        </v-col>
                        <v-col cols="12" sm="3" md="2">
                            <v-text-field
                                v-if="!contract.singleDay"
                                v-model="contract.duration"
                                :label="$t('day', 2)"
                                variant="outlined"
                                color="primary"
                                density="compact"
                                type="number"
                                hide-spin-buttons
                                data-cy="duration"
                                width="100"
                                hide-details
                            />
                        </v-col>
                    </v-row>
                </v-radio-group>
            </v-col>
        </v-row>
        <v-row dense>
            <v-col cols="12" md="2">
                <span class="text-h6 text--secondary">
                    {{ $t('start_end_points') }}
                </span>
            </v-col>
            <v-col cols="12" md="10">
                <InfoStartEndPointsForm v-model="contract" :service-type="contractServiceType.ownExcursion" />
            </v-col>
        </v-row>
    </div>
</template>

<script setup lang="ts">
    import {contractServiceType, type SelectOptionType, Sharing, Transportation} from '~/utils/types/types'
    import {timeFormatRule} from '~/composables/rules'
    import InfoStartEndPointsForm from '~/components/products/information/InfoStartEndPointsForm.vue'

    const props = defineProps<{
        editMode?: boolean
    }>()

    const contract = defineModel<any>()
    const variants = ref<SelectOptionType[]>([])

    const app = useNuxtApp()
    const {t} = useI18n()

    onMounted(async () => {
        await loadVariants()
    })

    async function loadVariants() {
        try {
            const productType = contractServiceType.ownExcursion
            const {productVariants} = await app.$api.productVariants.get({
                productType,
            })
            variants.value = productVariants.map(({id, name}) => ({value: id, title: name}))
        } catch (e) {
            variants.value = []
        }
    }

    const formatTime = (event, key): void => {
        const filteredValue = event.replace(/[^0-9:]/g, '')
        const formattedValue = filteredValue.replace(/(\d{2})(?=\d)/g, '$1:')
        contract.value[key] = formattedValue
    }

    const useSharing = computed(() => {
        const sharingTypes: Sharing[] = [Sharing.Shared, Sharing.Private]
        return sharingTypes.map(value => ({value, title: t(`sharingType.${value}`)}))
    })

    const useTransportation = computed(() => {
        const transportations: Transportation[] = [Transportation.Walking, Transportation.OffSite]
        return transportations.map(value => ({value, title: t(`transportationType.${value}`)}))
    })
</script>
